// Sprites dos Pokémon.
// - Animados (preferência): folhas do Pokémon Mystery Dungeon com 8 direções e
//   as animações idle/walk/attack/hurt (npm run anim → assets/pmd-index.json).
//   No HTML viram uma animação CSS (idle de frente); no PixiJS, PokemonSprite.
// - Estáticos (fallback): atlas dos sprites da PokéAPI (npm run atlas).
import { Assets, Rectangle, Spritesheet, Texture } from "pixi.js";

interface Frame { frame: { x: number; y: number; w: number; h: number }; spriteSourceSize: { x: number; y: number }; sourceSize: { w: number; h: number } }
interface AtlasJson { frames: Record<string, Frame>; meta: { image: string; size: { w: number; h: number } } }

export type AnimName = "idle" | "walk" | "attack" | "hurt";
export interface AnimInfo { y: number; w: number; h: number; dirs: 1 | 8; durations: number[]; hit?: number }
interface SheetInfo { file: string; source: "pmd" | "showdown"; anims: Partial<Record<AnimName, AnimInfo>> }
type Variant = "normal" | "shiny";

let manifest: Record<Variant, { json: string; png: string }> | null = null;
let pmdIndex: Record<Variant, Record<string, SheetInfo>> = { normal: {}, shiny: {} };
const atlases: Partial<Record<Variant, AtlasJson>> = {};
const sheets: Partial<Record<Variant, Spritesheet>> = {};
let loading: Promise<void> | null = null;

export function loadSprites(): Promise<void> {
  loading ??= (async () => {
    const [m, idx] = await Promise.all([
      fetch("/assets/atlas-manifest.json").then((r) => r.json()),
      fetch("/assets/pmd-index.json").then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]);
    manifest = m;
    if (idx) pmdIndex = idx;
    await Promise.all(
      (["normal", "shiny"] as const).map(async (v) => {
        atlases[v] = await (await fetch(`/assets/${manifest![v].json}`)).json();
      }),
    );
  })();
  return loading;
}

export async function loadPixiSheets(): Promise<void> {
  await loadSprites();
  for (const v of ["normal", "shiny"] as const) {
    sheets[v] ??= await Assets.load<Spritesheet>(`/assets/${manifest![v].json}`);
  }
}

/** Textura estática (atlas). */
export function texture(species: number, shiny = false): Texture | null {
  const v = shiny ? "shiny" : "normal";
  return sheets[v]?.textures[`${v}/${species}`] ?? null;
}

// ------------------------------------------------------------ animados
export function animInfo(species: number, shiny = false): SheetInfo | undefined {
  return pmdIndex[shiny ? "shiny" : "normal"][species] ?? (shiny ? pmdIndex.normal[species] : undefined);
}

export interface LoadedAnim {
  info: SheetInfo;
  /** quadros por animação e direção (0 = baixo, sentido horário até 7 = baixo-esquerda) */
  frames: Partial<Record<AnimName, Texture[][]>>;
}
const animCache = new Map<string, Promise<LoadedAnim | null>>();
const animReady = new Map<string, LoadedAnim>();

export function loadAnim(species: number, shiny = false): Promise<LoadedAnim | null> {
  const key = `${shiny ? "s" : "n"}${species}`;
  let p = animCache.get(key);
  if (!p) {
    p = (async () => {
      await loadSprites();
      const info = animInfo(species, shiny);
      if (!info) return null;
      try {
        const base = await Assets.load<Texture>(`/assets/${info.file}`);
        base.source.scaleMode = "nearest";
        const frames: LoadedAnim["frames"] = {};
        for (const [name, a] of Object.entries(info.anims) as [AnimName, AnimInfo][]) {
          frames[name] = Array.from({ length: a.dirs }, (_, d) =>
            a.durations.map((_, i) => new Texture({ source: base.source, frame: new Rectangle(i * a.w, a.y + d * a.h, a.w, a.h) })),
          );
        }
        const loaded = { info, frames };
        animReady.set(key, loaded);
        return loaded;
      } catch {
        return null;
      }
    })();
    animCache.set(key, p);
  }
  return p;
}

/** Já carregado? (para montar a unidade sem esperar) */
export function animNow(species: number, shiny = false): LoadedAnim | undefined {
  return animReady.get(`${shiny ? "s" : "n"}${species}`);
}

/** Pré-carrega as folhas de várias espécies em segundo plano (4 por vez). */
export function preloadAnims(species: Iterable<number>, shiny = false): Promise<void> {
  const queue = [...new Set(species)];
  return Promise.all(Array.from({ length: 4 }, async () => {
    for (let s = queue.shift(); s !== undefined; s = queue.shift()) await loadAnim(s, shiny);
  })).then(() => undefined);
}

// --------------------------------------------------------------- HTML
const injected = new Set<string>();
function keyframes(name: string, a: AnimInfo): void {
  if (injected.has(name)) return;
  injected.add(name);
  const total = a.durations.reduce((s, d) => s + d, 0);
  let acc = 0;
  const steps = a.durations.map((d, i) => {
    const pct = ((acc / total) * 100).toFixed(3);
    acc += d;
    return `${pct}%{background-position:${-i * a.w}px ${-a.y}px}`;
  });
  const style = document.createElement("style");
  style.textContent = `@keyframes ${name}{${steps.join("")}100%{background-position:${-(a.durations.length - 1) * a.w}px ${-a.y}px}}`;
  document.head.appendChild(style);
}

/** Recorte do sprite estático no atlas (para desenhar em canvas, ex.: card de compartilhar). */
export function atlasFrame(species: number, shiny = false): { url: string; x: number; y: number; w: number; h: number } | null {
  const v = shiny ? "shiny" : "normal";
  const atlas = atlases[v];
  const f = atlas?.frames[`${v}/${species}`];
  if (!atlas || !f || !manifest) return null;
  return { url: `/assets/${manifest[v].png}`, x: f.frame.x, y: f.frame.y, w: f.frame.w, h: f.frame.h };
}

/** <span> com o Pokémon: animado (idle de frente) quando houver, senão o sprite estático do atlas. */
export function spriteHtml(species: number, size = 64, shiny = false): string {
  const info = animInfo(species, shiny);
  const idle = info?.anims.idle;
  if (info && idle) {
    const name = `pmd-${shiny ? "s" : "n"}${species}`;
    keyframes(name, idle);
    const total = idle.durations.reduce((s, d) => s + d, 0) / 60;
    // escala: Pokémon pequenos ficam do mesmo tamanho; os grandes encolhem para caber
    const k = Math.min(size / 28, (size * 1.4) / Math.max(idle.w, idle.h));
    return `<span class="sprite" style="width:${size}px;height:${size}px"><span class="pmd" style="
      width:${idle.w}px;height:${idle.h}px;transform:translate(-50%,-50%) scale(${k.toFixed(3)});
      background:url(/assets/${info.file}) 0 ${-idle.y}px no-repeat;animation:${name} ${total.toFixed(2)}s step-end infinite"></span></span>`;
  }
  const v = shiny ? "shiny" : "normal";
  const atlas = atlases[v];
  const f = atlas?.frames[`${v}/${species}`];
  if (!atlas || !f || !manifest) return `<span class="sprite" style="width:${size}px;height:${size}px"></span>`;
  const k = size / f.sourceSize.w;
  return `<span class="sprite" style="width:${size}px;height:${size}px"><span style="
    position:absolute;left:${f.spriteSourceSize.x * k}px;top:${f.spriteSourceSize.y * k}px;
    width:${f.frame.w * k}px;height:${f.frame.h * k}px;
    background:url(/assets/${manifest[v].png}) -${f.frame.x * k}px -${f.frame.y * k}px / ${atlas.meta.size.w * k}px ${atlas.meta.size.h * k}px no-repeat;
    image-rendering:pixelated"></span></span>`;
}
